import axios from 'axios';
import { store } from '../store';
import { logout, refreshToken } from '../store/slices/authSlice';

// Determine API URL based on environment
const getApiUrl = (): string => {
  // 1. Use explicit environment variable if set
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 2. Detect environment based on hostname
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Local development
    return 'http://localhost:5004/api';
  } else if (hostname.includes('staging')) {
    // Staging environment
    return 'https://collegian-api-staging-584170663933.us-central1.run.app/api';
  } else {
    // Production environment (collegeiq.ai or Cloud Run production URL)
    return 'https://collegian-api-584170663933.us-central1.run.app/api';
  }
};

// Create axios instance with base URL
const api = axios.create({
  baseURL: getApiUrl(),
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true // Include cookies in requests
});

// Set auth token for all requests
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Export the API instance as a named export
export { api };

// Intercept responses to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 Unauthorized and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        await store.dispatch(refreshToken()).unwrap();
        
        // Get the new token from the store
        const state = store.getState();
        const newToken = state.auth.token;
        
        if (newToken) {
          // Update the failed request with the new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // If refresh fails, log out
        store.dispatch(logout());
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API is already exported as a named export
